import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getLoginUserData } from '../services/account.service';
import { UserDataType } from '../validation/business.validation';

interface UserData extends UserDataType {
  [key: string]: any; // Allow additional fields
}

interface BusinessData {
  [key: string]: any; // Business profile data
}

interface UserContextType {
  user: UserData | null;
  business: BusinessData | null;
  loading: boolean;
  error: string | null;
  refetchUser: () => void;
  setUser: (user: UserData | null) => void;
  setBusiness: (business: BusinessData | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [business, setBusiness] = useState<BusinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await getLoginUserData();
      setUser(userData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch user data');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const refetchUser = () => {
    fetchUser();
  };

  return (
    <UserContext.Provider value={{ user, business, loading, error, refetchUser, setUser, setBusiness }}>
      {children}
    </UserContext.Provider>
  );
};